import * as Sentry from '@sentry/react'
import { BrowserTracing } from '@sentry/tracing'
// eslint-disable-next-line import/no-cycle
import config from '../../config'
import * as constants from '../../constants'
import * as Tracker from '../Tracker'
import * as trackerInternal from '../tracker-internal/trackerInternal'
import fullstory from './vendors/fullstory'
import hotjar from './vendors/hotjar'
import intercom from './vendors/intercom'
import mixpanel from './vendors/mixpanel'
import beamer from './vendors/beamer/beamer'
import { BEAMER_SELECTOR_ID } from '../constants'
// eslint-disable-next-line import/no-cycle
import { getUserInfo } from './common-user-info'
// eslint-disable-next-line import/no-cycle
import { getPermission } from '../../common/wscjs/user-info'
import { TRACKER_CONTEXT } from '../constants/trackerConstants'
import { APP } from '../../common/topbar/consts'

export const ANALYTICS = Object.freeze({
  SENTRY: 'SENTRY',
  FULLSTORY: 'FULLSTORY',
  MIXPANEL: 'MIXPANEL',
  INTERCOM: 'INTERCOM',
  HOTJAR: 'HOTJAR',
  BEAMER: 'BEAMER',
})

let unreadPostsCount = 0

export const SENTRY_RESPONSIBILITY_DOMAIN = Object.freeze({
  GRAPHICS: 'Graphics',
  CMS: 'CMS',
  CREATE_VIDEO: 'Create Video',
})

export function initAnalytics(init) {
  if (init[ANALYTICS.SENTRY]) {
    const sampleRate = Number.parseFloat(config.SENTRY_SAMPLE_RATE, 10) || 1.0

    Sentry.init({
      dsn: init[ANALYTICS.SENTRY],
      release: config.RELEASE,
      environment: config.ENV,
      integrations: [new BrowserTracing()],
      sampleRate: sampleRate,
      tracesSampleRate: sampleRate,
      ignoreErrors: [
        // General recurring errors
        'Feature not supported',
        'Failed to fetch',
        'Not Found',
        'The request timed out.',
        'The network connection was lost.', // Safari
        'NetworkError when attempting to fetch resource.', // Firefox general fetch fail message
        "Unexpected token '<'",
        '404 Not Found',
        'ResizeObserver', // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
        'Failed to load because no supported source was found.',
        'The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission', // Prolly some iOS specific error
        "Can't find variable: AbortController",
        'AbortError: AbortError',
        "Object doesn't support property or method 'finally'",

        // WSC code bugs that won't be solved soon
        'Cannot redefine property: snapshot', // https://github.com/mobxjs/mobx/issues/1876
        `can't redefine non-configurable property "snapshot"`,
        'Attempting to change configurable attribute of unconfigurable property', // Another specific Mobx-state-tree issue
        'InvalidStateError: The object is in an invalid state.',

        // SignalR errors we don't know what to do with yet
        'Error parsing handshake response: Error: Expected a handshake response from the server.',
        'Server timeout elapsed without receiving a message from the server.',
        'WebSocket closed with status code: 1006 ().',
        'WebSocket closed with status code: 1000 ().',
        'Non-Error promise rejection captured with value: Server returned handshake error: Handshake was canceled.',
        "Cannot send data if the connection is not in the 'Connected' State.",
        'Server returned handshake error: Handshake was canceled.',

        // Suppress all kinds of `AbortError` when aborting an AbortController on Firefox (I still don't know how to catch this (David))
        // And other mainly Safari issues
        'The operation was aborted.',
        'AbortError: The operation was aborted.',
        'AbortError: Fetch is aborted', // Safari
        'AbortController is not defined', // Though luck

        // Suppress `AbortError` when <video /> urls are changing in FireFox
        "The fetching process for the media resource was aborted by the user agent at the user's request.",

        // Silent this for now. handle later
        'Unauthorized',

        // Default fetch errors for a stale window. Stale window should refresh next time they are focused
        "Window's session is stale",

        // Temp
        "Cannot read property 'play' of null",

        'AbortError: The play() request was interrupted by a call to pause().',
        'AbortError: The play() request was interrupted because the media was removed from the document.',
      ],
      beforeBreadcrumb(breadcrumb, hint) {
        // Filter breadcrumbs for vendor services
        if (/^(fetch|xhr)$/.test(breadcrumb.category)) {
          if (
            breadcrumb.data &&
            breadcrumb.data.url &&
            /(mixpanel.com|fullstory.com)/.test(breadcrumb.data.url)
          ) {
            return null
          }
        }

        if (breadcrumb.category === 'ui.click') {
          if (hint.event && hint.event.target) {
            if (hint.event.target.getAttribute('data-type')) {
              breadcrumb.message = hint.event.target.getAttribute('data-type')
            }
          }
        }

        return breadcrumb
      },
    })
  }

  setIsTrackerEnabled(init[ANALYTICS.MIXPANEL])
  initMixpanel(init[ANALYTICS.MIXPANEL], window.isTrackerEnabled)

  if (init[ANALYTICS.FULLSTORY]) {
    fullstory(config.WEBSITE === 'bqpanel')
  }

  if (init[ANALYTICS.INTERCOM]) {
    if (config.ENV === 'prod') {
      intercom('sedsp6o7', config.IS_CHINA_ENV)
    } else if (config.ENV === 'uat') {
      intercom('lzm4pof2')
    }
  }

  if (init[ANALYTICS.BEAMER]) {
    const userInfo = getUserInfo()
    const permissions = getPermission()

    beamer({
      product_id: 'rqDbwbtL65228',
      selector: BEAMER_SELECTOR_ID,
      button: false,
      filter: `${permissions?.group?.id}`,
      user_firstname: userInfo?.user?.firstName,
      user_lastname: userInfo?.user?.lastName,
      user_email: userInfo?.user?.email,
      user_id: userInfo?.user?.id,
      callback: (count) => {
        unreadPostsCount = count
      },
      onopen: () => {
        const currentApp = window.location.pathname.split('/')[1]
        let product = ''

        if (currentApp === APP.GRAPHICS || currentApp === APP.AVGEN) {
          product = currentApp
        } else {
          product = window.location.pathname.split('/')[2]
        }

        Tracker.track({
          eventName: constants.TRACKER_EVENT_COMMON.BEAMER_BUTTON_CLICK,
          context: TRACKER_CONTEXT.SIDE_BAR_MENU,
          userAliasId: userInfo?.user?.id,
          product,
          unreadPostsCount,
        })

        unreadPostsCount = 0
      },
    })
  }

  if (init[ANALYTICS.HOTJAR]) {
    hotjar()
  }
}

export function setAnalyticsInfo(userId, options) {
  const userData = {
    userAliasName: options.username,
    userAliasId: userId,
    wscWorkerId: options.wscWorkerId,
    userGroupName: options.groupName,
    userGroupId: options.groupId,
    systemName: options.systemName,
    systemId: options.systemId,
    systemRole: options.systemRole,
    customerId: options.customerId,
    customerName: options.customerName,
    customerRole: options.customerRole,
    user: options.username, // For Mixpanel backwards compatibility. Should removed with Mixpanel old project.
    group: options.groupName, // For Mixpanel backwards compatibility. Should removed with Mixpanel old project.
    system: options.systemName, // For Mixpanel backwards compatibility. Should removed with Mixpanel old project.
    customer: options.clientName, // For Mixpanel backwards compatibility. Should removed with Mixpanel old project.
  }

  if (window.__SENTRY__) {
    Sentry.configureScope((scope) => {
      const { userAliasName, userAliasId, ...sentryUserData } = userData

      sentryUserData.id = userAliasId
      sentryUserData.username = userAliasName

      scope.setUser(sentryUserData)

      Object.keys(userData).forEach((key) => {
        scope.setTag(key, sentryUserData[key])
      })
    })
  }

  const trackerUserData = { ...userData }
  const utmId = new URLSearchParams(window.location.search).get('utm_id')
  if (utmId) {
    trackerUserData.utmId = utmId
  }

  if (window.isTrackerEnabled) {
    Tracker.identify(userId)
    Tracker.setUserInfo(trackerUserData)

    trackerInternal.initiliazeMetaData({
      customerName: userData.customerName,
      customerRole: userData.customerRole,
      userAliasName: userData.userAliasName,
      userGroupId: userData.userGroupId,
      userGroupName: userData.userGroupName,
      systemRole: userData.systemRole,
      systemName: userData.systemName,
      wscWorkerId: userData.wscWorkerId,
    })
  }

  // FullStory handles tracking inside same-origin iFrames so it exposes FS but FS.identify is not
  // available and is useless anyway
  if (window.FS && window.FS.identify && typeof window.FS.identify === 'function') {
    window.FS.identify(userId, options)
  }

  if (window.Intercom) {
    window.Intercom('update', {
      user_id: userId,
      email: options.email,
      name: options.displayName,
      isAdmin: options.isAdmin,
      'First name': options.firstName,
      'Last name': options.lastName,
      Sport: options.systemTypeName,
      'Sport Id': options.systemTypeId,
      'All Sports_list': options.allSports,
      Group: options.groupName,
      System: options.systemName,
      System_id: options.systemId,
      'Customer name': options.customerName,
      'Customer Id': options.customerId,
    })
  }

  if (window.hj) {
    window.hj('identify', userId, options)
  }
}

export function setContext(name, data) {
  Sentry.setContext(name, data)
}

export function logException(ex, extras, fingerprint, tags) {
  captureException('error', ex, extras, fingerprint, tags)
}

export function logInfo(ex, extras, fingerprint) {
  captureException('info', ex, extras, fingerprint)
}

function captureException(level, ex, extras, fingerprint, tags) {
  Sentry.withScope((scope) => {
    scope.setLevel(level)

    if (extras) {
      scope.setExtras(extras)
    }

    if (fingerprint) {
      scope.setFingerprint(fingerprint)
    }

    if (tags) {
      scope.setTags(tags)
    }

    Sentry.captureException(ex)
  })
}

export function captureMessage(message, extras) {
  Sentry.withScope((scope) => {
    if (extras) {
      scope.setExtras(extras)
    }

    Sentry.captureMessage(message)
  })
}

export function logGraphicsError({ exception, description, cta, templateId = undefined }) {
  logException(exception, null, null, {
    responsibility_domain: SENTRY_RESPONSIBILITY_DOMAIN.GRAPHICS,
    description,
    cta,
    templateId,
  })
}

export function logCMSError({ exception, description, cta }) {
  logException(exception, null, null, {
    responsibility_domain: SENTRY_RESPONSIBILITY_DOMAIN.CMS,
    description,
    cta,
  })
}

export function logCreateVideoError({ exception, description, cta }) {
  logException(exception, null, null, {
    responsibility_domain: SENTRY_RESPONSIBILITY_DOMAIN.CREATE_VIDEO,
    description,
    cta,
  })
}

function setIsTrackerEnabled(isEnvironmentTrackerEnabled) {
  const userInfo = getUserInfo()

  const isAutomationUser = userInfo?.customer?.roleId === constants.AUTOMATION_ROLE_ID

  window.isTrackerEnabled = isEnvironmentTrackerEnabled && !isAutomationUser
}

function initMixpanel(mixpanelConfigValue, isTrackerEnabled) {
  if (!isTrackerEnabled) {
    return
  }

  const projectToken = getMixpanelProjectToken()

  if (!projectToken) {
    return
  }

  mixpanel(projectToken, config.IS_CHINA_ENV)

  // Should be removed when mixpanel depracted project will deleted
  const depractedProjectToken = mixpanelConfigValue
  if (window.mixpanel) {
    window.mixpanel.init(depractedProjectToken, {}, 'depractedProject')
  }
}

function getMixpanelProjectToken() {
  const isPlatformApp =
    config.WEBSITE === constants.CLIPRO_APP_NAME ||
    config.WEBSITE === constants.AVGEN_APP_NAME ||
    config.WEBSITE === constants.GRAPHICS_APP_NAME ||
    config.WEBSITE === constants.LOGIN_APP_NAME

  if (isPlatformApp) {
    if (config.ENV === constants.ENVIRONMENTS.PROD) {
      return 'a76b1cd8aa45497a107a73c08f793812'
    } else if (config.ENV === constants.ENVIRONMENTS.UAT) {
      return '6bbe45b05a4eaaf1c43abe117c7aed97'
    }
  }

  return null
}
